// Account-link-first save flow (plans/value-calculator-account-link-save.md Unit 2) — pure list
// logic behind `AccountLinkMenu`: the New/Existing partition, the recencyKey sort (nulls last,
// name asc tie-break), the client-side name filter, and the per-account opportunity-click
// behavior. Extracted into its own leaf module (no React imports) so it's unit-testable without
// mounting the component — this repo's `bun test` has no DOM (see
// `mini-apps/action-view/__tests__/notes-save-mutation.test.tsx` for the same constraint
// documented elsewhere), but none of this logic needs a DOM at all, only plain data.

import type { OwnedAccount, OwnedAccountOpenOpportunity } from '../api-client';
import { money } from '../engine/format';

export type AccountLinkTab = 'new' | 'existing';

/** New tab: accounts with no saved snapshot. Existing tab: accounts with one. */
export function partitionAccounts(accounts: OwnedAccount[]): {
  newAccounts: OwnedAccount[];
  existingAccounts: OwnedAccount[];
} {
  const newAccounts: OwnedAccount[] = [];
  const existingAccounts: OwnedAccount[] = [];
  for (const account of accounts) {
    (account.latestSnapshot == null ? newAccounts : existingAccounts).push(account);
  }
  return { newAccounts, existingAccounts };
}

/**
 * Sort by `recencyKey` desc (ISO date strings compare correctly as plain strings), nulls last,
 * name asc tie-break (also the tie-break when both `recencyKey`s are absent). Returns a new
 * array — never mutates the input.
 */
export function sortAccountsByRecency(accounts: OwnedAccount[]): OwnedAccount[] {
  return [...accounts].sort((a, b) => {
    if (a.recencyKey && b.recencyKey) {
      if (a.recencyKey !== b.recencyKey) return a.recencyKey < b.recencyKey ? 1 : -1;
      return a.name.localeCompare(b.name);
    }
    if (a.recencyKey && !b.recencyKey) return -1;
    if (!a.recencyKey && b.recencyKey) return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Case-insensitive substring match on account name over the already-fetched list (no
 * per-keystroke network — the whole owned-accounts list is fetched once, on menu open). A
 * blank/whitespace-only query returns the list unchanged.
 */
export function filterAccountsByName(accounts: OwnedAccount[], query: string): OwnedAccount[] {
  const q = query.trim().toLowerCase();
  if (!q) return accounts;
  return accounts.filter((account) => account.name.toLowerCase().includes(q));
}

/**
 * The tab's visible list: partition → filter → sort, in that order (filtering before sorting is
 * equivalent either way here, but partition must happen first — New/Existing is a property of
 * `latestSnapshot`, not something the filter or sort should see mixed together).
 */
export function visibleAccountsForTab(
  accounts: OwnedAccount[],
  tab: AccountLinkTab,
  query: string,
): OwnedAccount[] {
  const { newAccounts, existingAccounts } = partitionAccounts(accounts);
  const base = tab === 'new' ? newAccounts : existingAccounts;
  return sortAccountsByRecency(filterAccountsByName(base, query));
}

/**
 * Account card opportunity behavior (plan Unit 2):
 *   0 open opps  → 'link'   — clicking links the account with no opportunity.
 *   1 open opp   → 'auto'   — its name is the card subtitle; clicking links account + that opp
 *                             automatically.
 *   2+ open opps → 'expand' — clicking expands the card into one row per opp; picking a row
 *                             completes the link with that opp.
 */
export type OpportunityMode =
  | { kind: 'link' }
  | { kind: 'auto'; opportunity: OwnedAccountOpenOpportunity }
  | { kind: 'expand' };

export function opportunityModeFor(account: OwnedAccount): OpportunityMode {
  const opps = account.openOpportunities;
  if (opps.length === 0) return { kind: 'link' };
  if (opps.length === 1) return { kind: 'auto', opportunity: opps[0] };
  return { kind: 'expand' };
}

/**
 * Formats the Existing tab card's total-annual-value caption. `null` (no value on the snapshot)
 * renders as an empty string rather than `money()`'s own `NaN`/non-finite fallback of `$0` — a
 * missing value should show nothing, not a misleading zero-dollar figure.
 */
export function formatAnnualValue(value: number | null): string {
  return value == null ? '' : money(value);
}

/** Formats an ISO date string for the "last saved" card caption. */
export function formatSavedDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
